import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  Analytics as AnalyticsIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import marketingService from '../services/marketingService';
import EmailComposer from '../components/EmailComposer';
import CampaignAnalytics from '../components/CampaignAnalytics';
import SchedulePicker from '../components/SchedulePicker';

export default function EmailCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuCampaign, setMenuCampaign] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadCampaigns();
  }, [statusFilter]);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (statusFilter) filters.status = statusFilter;
      if (searchTerm) filters.search = searchTerm;

      const data = await marketingService.getCampaigns(filters);
      setCampaigns(data.campaigns);
    } catch (error) {
      showAlert('Failed to load campaigns', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenComposer = (campaign = null) => {
    setSelectedCampaign(campaign);
    setComposerOpen(true);
    handleCloseMenu();
  };

  const handleCloseComposer = () => {
    setComposerOpen(false);
    setSelectedCampaign(null);
  };

  const handleSaveCampaign = async (campaignData) => {
    try {
      if (selectedCampaign) {
        await marketingService.updateCampaign(selectedCampaign.id, campaignData);
        showAlert('Campaign updated successfully');
      } else {
        await marketingService.createCampaign(campaignData);
        showAlert('Campaign created successfully');
      }
      handleCloseComposer();
      loadCampaigns();
    } catch (error) {
      showAlert('Failed to save campaign', 'error');
    }
  };

  const handleSendCampaign = async (campaign) => {
    if (!window.confirm(`Send campaign "${campaign.name}" now?`)) return;

    try {
      await marketingService.sendCampaign(campaign.id);
      showAlert('Campaign sent successfully');
      loadCampaigns();
    } catch (error) {
      showAlert('Failed to send campaign', 'error');
    }
    handleCloseMenu();
  };

  const handleScheduleCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setScheduleOpen(true);
    handleCloseMenu();
  };

  const handleConfirmSchedule = async (scheduledAt) => {
    try {
      await marketingService.scheduleCampaign(selectedCampaign.id, scheduledAt);
      showAlert('Campaign scheduled successfully');
      setScheduleOpen(false);
      loadCampaigns();
    } catch (error) {
      showAlert('Failed to schedule campaign', 'error');
    }
  };

  const handleViewAnalytics = (campaign) => {
    setSelectedCampaign(campaign);
    setAnalyticsOpen(true);
    handleCloseMenu();
  };

  const handleDeleteCampaign = async (campaign) => {
    if (!window.confirm(`Delete campaign "${campaign.name}"?`)) return;

    try {
      await marketingService.deleteCampaign(campaign.id);
      showAlert('Campaign deleted successfully');
      loadCampaigns();
    } catch (error) {
      showAlert('Failed to delete campaign', 'error');
    }
    handleCloseMenu();
  };

  const handleMenuOpen = (event, campaign) => {
    setAnchorEl(event.currentTarget);
    setMenuCampaign(campaign);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuCampaign(null);
  };

  const showAlert = (message, severity = 'success') => {
    setAlert({ open: true, message, severity });
    setTimeout(() => setAlert({ ...alert, open: false }), 3000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent': return 'success';
      case 'scheduled': return 'info';
      case 'draft': return 'default';
      default: return 'default';
    }
  };

  return (
    <Box>
      {alert.open && (
        <Alert severity={alert.severity} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            placeholder="Search campaigns..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && loadCampaigns()}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="scheduled">Scheduled</MenuItem>
            <MenuItem value="sent">Sent</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} md={3}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenComposer()}
            sx={{ height: '56px' }}
          >
            New Campaign
          </Button>
        </Grid>
      </Grid>

      <TableContainer component={Card}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Recipients</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Sent</TableCell>
              <TableCell>Opens</TableCell>
              <TableCell>Clicks</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.map((campaign) => (
              <TableRow key={campaign.id}>
                <TableCell>{campaign.name}</TableCell>
                <TableCell>{campaign.subject}</TableCell>
                <TableCell>
                  <Chip label={campaign.recipient_type} size="small" />
                </TableCell>
                <TableCell>
                  <Chip
                    label={campaign.status}
                    color={getStatusColor(campaign.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>{campaign.sent_count || 0}</TableCell>
                <TableCell>
                  {campaign.open_count || 0} ({campaign.open_rate || 0}%)
                </TableCell>
                <TableCell>
                  {campaign.click_count || 0} ({campaign.click_rate || 0}%)
                </TableCell>
                <TableCell>
                  <IconButton onClick={(e) => handleMenuOpen(e, campaign)}>
                    <MoreIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        {menuCampaign?.status === 'draft' && (
          <MenuItem onClick={() => handleOpenComposer(menuCampaign)}>
            <EditIcon sx={{ mr: 1 }} /> Edit
          </MenuItem>
        )}
        {menuCampaign?.status === 'draft' && (
          <MenuItem onClick={() => handleSendCampaign(menuCampaign)}>
            <SendIcon sx={{ mr: 1 }} /> Send Now
          </MenuItem>
        )}
        {menuCampaign?.status === 'draft' && (
          <MenuItem onClick={() => handleScheduleCampaign(menuCampaign)}>
            <ScheduleIcon sx={{ mr: 1 }} /> Schedule
          </MenuItem>
        )}
        {menuCampaign?.status === 'sent' && (
          <MenuItem onClick={() => handleViewAnalytics(menuCampaign)}>
            <AnalyticsIcon sx={{ mr: 1 }} /> Analytics
          </MenuItem>
        )}
        {menuCampaign?.status !== 'sent' && (
          <MenuItem onClick={() => handleDeleteCampaign(menuCampaign)}>
            <DeleteIcon sx={{ mr: 1 }} /> Delete
          </MenuItem>
        )}
      </Menu>

      <Dialog
        open={composerOpen}
        onClose={handleCloseComposer}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {selectedCampaign ? 'Edit Campaign' : 'New Campaign'}
        </DialogTitle>
        <DialogContent>
          <EmailComposer
            campaign={selectedCampaign}
            onSave={handleSaveCampaign}
            onCancel={handleCloseComposer}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={analyticsOpen}
        onClose={() => setAnalyticsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Campaign Analytics</DialogTitle>
        <DialogContent>
          {selectedCampaign && (
            <CampaignAnalytics campaignId={selectedCampaign.id} />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Schedule Campaign</DialogTitle>
        <DialogContent>
          <SchedulePicker
            onConfirm={handleConfirmSchedule}
            onCancel={() => setScheduleOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
